// src/components/admin/TestPromptModal.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Button,
  FloatingLabel,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";

/**
 * Props:
 *  - show (bool)
 *  - onHide () => void
 *  - initialData: {
 *        prompt_text: string,
 *        model: string,
 *        parameters?: { [key:string]: string }
 *    }
 *
 * Behavior:
 *  - Parses {placeholders} from prompt_text to build parameters map.
 *  - Lets user edit prompt_text, model, and parameter values.
 *  - Calls /try_prompt directly.
 */
export default function TestPromptModal({ show, onHide, initialData }) {
  const [localPrompt, setLocalPrompt] = useState(initialData?.prompt_text || "");
  const [localModel, setLocalModel] = useState(initialData?.model || "");
  const [localParams, setLocalParams] = useState(initialData?.parameters || {});
  const [testResult, setTestResult] = useState("");
  const [testError, setTestError] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // 🔍 helper: extract {param_name} from prompt text
  const extractParams = useCallback((tpl, existingParams = {}) => {
    const regex = /{([a-zA-Z0-9_]+)}/g;
    const found = new Set();
    let m;
    while ((m = regex.exec(tpl)) !== null) {
      found.add(m[1]);
    }

    const nextMap = {};
    // keep old values if key still exists
    found.forEach((key) => {
      nextMap[key] = Object.prototype.hasOwnProperty.call(existingParams, key)
        ? existingParams[key]
        : "";
    });

    return nextMap;
  }, []);

  // whenever modal is opened with new data → reset internal state
  useEffect(() => {
    const prompt_text = initialData?.prompt_text || "";
    const model = initialData?.model || "";
    // prefer caller-provided parameters if any, else extract fresh
    const baseParams =
      initialData?.parameters && Object.keys(initialData.parameters).length
        ? initialData.parameters
        : extractParams(prompt_text);

    setLocalPrompt(prompt_text);
    setLocalModel(model);
    setLocalParams(baseParams);
    setTestResult("");
    setTestError(null);
    setTestLoading(false);
  }, [show, initialData, extractParams]);

  // when prompt changes, we dynamically recompute param keys
  const handlePromptChange = (val) => {
    setLocalPrompt(val);
    setLocalParams((prev) => extractParams(val, prev));
  };

  // hit backend /try_prompt
  const handleRunTest = useCallback(async () => {
  setTestLoading(true);
  setTestError(null);
  setTestResult("");

  try {
    if (!localModel.trim()) {
      throw new Error("Model is required");
    }

    // ✅ Build URL with query params
    const apiBase = process.env.REACT_APP_API_URL;
    const url = new URL(`${apiBase}/prompts/try_prompt`);
    url.searchParams.set("prompt", localPrompt);
    url.searchParams.set("model", localModel);

    // ✅ Send parameters dict as JSON body
    const r = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(localParams || {}),
    });

    const j = await r.json();

    if (!r.ok || j.success === false) {
      throw new Error(j?.errorObject?.message || j?.detail || "Test failed");
    }

    setTestResult(j.results || "");
  } catch (e) {
    setTestError(e?.message || "Something went wrong");
  } finally {
    setTestLoading(false);
  }
}, [localPrompt, localParams, localModel]);


  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Test Prompt</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Prompt Template */}
        <FloatingLabel label="Prompt Template">
          <Form.Control
            as="textarea"
            style={{ minHeight: 140 }}
            value={localPrompt}
            onChange={(e) => handlePromptChange(e.target.value)}
            placeholder="Write or paste your prompt template with {variables}"
          />
        </FloatingLabel>

        {/* Model (required) */}
        <div className="mt-3">
          <FloatingLabel label="Model (required)">
            <Form.Control
              type="text"
              value={localModel}
              onChange={(e) => setLocalModel(e.target.value)}
              placeholder="e.g. GPT-5"
              required
            />
          </FloatingLabel>
          <div className="small text-muted mt-1">
            Passed as <code>model</code> to /try_prompt.
          </div>
        </div>

        {/* Parameters */}
        <div className="mt-4">
          <div className="fw-semibold mb-2 d-flex align-items-center gap-2">
            <span>Parameters</span>
            <Badge bg="light" text="dark">
              auto-detected from {"{curly_braces}"}
            </Badge>
          </div>

          {Object.keys(localParams).length === 0 ? (
            <div className="text-muted small border rounded p-3">
              No {"{parameters}"} found in prompt. We'll send an empty dict.
            </div>
          ) : (
            <Row className="g-3">
              {Object.entries(localParams).map(([key, val]) => (
                <Col md={6} key={key}>
                  <FloatingLabel label={key}>
                    <Form.Control
                      type="text"
                      value={val}
                      placeholder={key}
                      onChange={(e) =>
                        setLocalParams((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                    />
                  </FloatingLabel>
                </Col>
              ))}
            </Row>
          )}
        </div>

        {/* Result / Error */}
        {(testResult || testError) && (
          <div className="mt-4">
            <div className="fw-semibold mb-2">Response</div>
            {testError ? (
              <Alert variant="danger" className="mb-2">
                {testError}
              </Alert>
            ) : null}
            {testResult ? (
              <Form.Control
                as="textarea"
                readOnly
                value={testResult}
                style={{ minHeight: 120, whiteSpace: "pre-wrap" }}
              />
            ) : null}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" onClick={onHide} disabled={testLoading}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleRunTest}
          disabled={testLoading || !localModel.trim()}
        >
          {testLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" /> Running…
            </>
          ) : (
            "Run Test"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
